import { useState } from 'react';

/**
 * End-of-article share row. Single-line, monospaced, terse —
 * matches the landing's terminal voice. Each target opens the
 * platform's submit URL in a new tab; "copy" writes the
 * canonical URL to the clipboard with a brief confirmation.
 *
 * The canonical post URL is composed at render time from the
 * provided `slug` against the production host so a share
 * triggered in dev (vite preview on localhost) still posts the
 * runmemento.com URL — sharing localhost URLs is the most
 * common smoke-test embarrassment with this kind of widget.
 */
export function ShareRow({
  title,
  slug,
}: {
  readonly title: string;
  readonly slug: string;
}): JSX.Element {
  const url = `https://runmemento.com/blogs/${slug}/`;
  const [copied, setCopied] = useState(false);

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — links are still usable. */
    }
  };

  // Share-link URL formats per platform (verified May 2026):
  //   X: `intent/post` (the post-Twitter-rebrand path; both `?text`
  //     and `?url` are honored, X composes them into a tweet).
  //   LinkedIn: `sharing/share-offsite` is the only reliable
  //     prefill — `?url` is the lone parameter LinkedIn actually
  //     respects. They strip text/title prefills.
  //   Reddit: `submit` opens the cross-subreddit picker so the
  //     sharer chooses r/LocalLLaMA, r/ClaudeAI, r/mcp, r/cursor,
  //     etc. — exactly the audience map for this post.
  // Hacker News and Bluesky were removed: HN should be the post
  // author's timed submission (don't let readers pre-empt the
  // launch window), and Bluesky's reach is too small at
  // cold-launch to earn a slot.
  const x = `https://x.com/intent/post?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const reddit = `https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`;

  return (
    <div className="mx-auto mt-16 max-w-prose border-t border-border pt-8">
      <p className="font-mono text-xs uppercase tracking-widish text-muted">share</p>
      <div className="mt-3 flex flex-wrap items-center gap-4 font-mono text-sm">
        <a
          href={x}
          target="_blank"
          rel="noopener noreferrer"
          className="text-fg underline decoration-border underline-offset-4 hover:decoration-accent"
        >
          X
        </a>
        <a
          href={linkedin}
          target="_blank"
          rel="noopener noreferrer"
          className="text-fg underline decoration-border underline-offset-4 hover:decoration-accent"
        >
          LinkedIn
        </a>
        <a
          href={reddit}
          target="_blank"
          rel="noopener noreferrer"
          className="text-fg underline decoration-border underline-offset-4 hover:decoration-accent"
        >
          Reddit
        </a>
        <button
          type="button"
          onClick={() => {
            void onCopy();
          }}
          aria-label={copied ? 'Copied URL' : 'Copy URL'}
          className="text-fg underline decoration-border underline-offset-4 hover:decoration-accent"
        >
          {copied ? 'copied' : 'copy link'}
        </button>
      </div>
    </div>
  );
}
